import React, { useEffect, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';

import Icons from 'assets/icons';

import { useGoalForm } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/context/context';
import useInitialize from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/hooks/useInitialize';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';
import { Radio } from 'ui/radio';
import { Select } from 'ui/select';
import { TextArea } from 'ui/text-area';
import { RadioGroup } from 'ui/radio-group';

import GoalValueInput from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/goal-value-input/goal-value-input';
import DetailFields from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/goal-details/goal-details';
import Button from 'components/partials/button/button';
import OtherGoalForm from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/other-goal-form/other-goal-form';
import { GeneratingTextArea } from 'ui/generating-text-area';

import { prepareGoalPayload } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goals-manager.helper';

import { schema } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-configuration/goals-configuration';

import {
  getValueLabel,
  validateValue,
} from 'containers/admin/clients/client/tabs-components/goals-tab/goals-manager/goal/goal-form/goal-form.helpers';

import { TYPES_WITHOUT_SUFFIXES } from 'containers/admin/clients/client/tabs-components/goals-tab/goals-tab.constants';
import { getTestId } from 'helpers/test-helpers';

import { generateGoalSummary } from 'api/generated/goal-summary-controller/goal-summary-controller';
import type { GoalSummaryRequest } from 'api/generated/schemas';

import type {
  Goal,
  SubTypeOption,
  FunnelMetricOption,
} from 'containers/admin/clients/client/tabs-components/goals-tab/types';

interface RadioOption {
  label: string;
  value: 'Increase' | 'Maintain' | 'Decrease';
  labelColor: string;
}

const radioOptions: RadioOption[] = [
  { label: 'Increase', value: 'Increase', labelColor: 'tokens.success.700' },
  { label: 'Maintain', value: 'Maintain', labelColor: 'tokens.gray.600' },
  { label: 'Decrease', value: 'Decrease', labelColor: 'tokens.critical.600' },
];

interface GoalFormProps {
  type: string;
  category: string;
  isEditing?: boolean;
  data?: Goal;
  isCreation: boolean;
  userId: string | undefined;
  enteringClassYear: number;
}

const GoalForm = ({
  type,
  category,
  isEditing,
  data,
  isCreation,
  userId,
  enteringClassYear,
}: GoalFormProps): React.JSX.Element => {
  const [generationLoading, setGenerationLoading] = useState<boolean>(false);
  const { control, setValue, reset, clearErrors, loading, getValues } =
    useGoalForm();

  const parsedType = type === 'Academic Quality' ? type.replace(' ', '') : type;

  const [valueType, setValueType] = useState<string | undefined>(
    schema[parsedType]?.valueType
  );
  const isDisabled = (Boolean(data) && !isEditing) || generationLoading;

  const initializeFormValues = useInitialize(type, setValue);

  const {
    fields: detailFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'details',
  });

  const generateSummary = async (): Promise<void> => {
    if (!userId) return;

    setGenerationLoading(true);

    const formData = getValues();

    const payload = prepareGoalPayload({
      userId,
      clientId: undefined,
      data: { ...(data ?? {}), type, category, enteringClassYear } as never,
      formData: formData as never,
    });

    try {
      const summaryPayload = payload.goalData as GoalSummaryRequest;

      const result = await generateGoalSummary(userId, summaryPayload);

      if (result?.summary) {
        setValue('summary', result.summary);
        clearErrors('summary');
      }
    } catch (error) {
      console.error('Error while generating AI description', error);
    } finally {
      setGenerationLoading(false);
    }
  };

  useEffect(() => {
    if (loading) return;

    if (category) {
      reset();

      if (data) {
        data.details?.forEach(() => append({}));
      }

      if (isCreation) {
        append({});
      }

      if (data && !isCreation) {
        initializeFormValues(
          data.details as Array<Record<string, unknown>>,
          data as Record<string, unknown>
        );
      }
    }
  }, [category, type, data, loading, isCreation]);

  useEffect(() => {
    const selectedSubType = schema[parsedType]?.subType?.find(
      (opt) => opt.value === data?.subType
    );

    if (selectedSubType) {
      setValueType(selectedSubType.valueType);
    } else {
      setValueType(schema[parsedType]?.valueType);
    }
  }, [data?.subType, type]);

  interface SelectOption {
    value: string;
    label: string;
  }

  const renderOptions = (
    options: Array<SubTypeOption | FunnelMetricOption>
  ): SelectOption[] =>
    options.map((option) => ({ value: option.value, label: option.label }));

  const getSelectedOptionValue = (
    options: Array<SubTypeOption | FunnelMetricOption>,
    value: unknown
  ): SelectOption | undefined => {
    const found = options.find((opt) => opt.value === value);

    return found ? { value: found.value, label: found.label } : undefined;
  };

  const handleSelectChange = (
    selectedOption: SelectOption,
    parsedTypeArg: string,
    schemaArg: typeof schema,
    onChange: (value: unknown) => void,
    setValueTypeArg: (v: string | undefined) => void
  ): void => {
    onChange(selectedOption.value);

    const newValueType = schemaArg[parsedTypeArg].subType?.find(
      (opt) => opt.value === selectedOption.value
    )?.valueType;

    setValueTypeArg(newValueType || schemaArg[parsedTypeArg].valueType);
  };

  const renderDetailFields = (): React.JSX.Element | null => {
    const isCombined = category === 'Combined';
    const isEditable = isCombined && (isEditing || isCreation);
    const parsedTypeLocal = type.replace(' ', '');

    const categoryConfig = schema[parsedTypeLocal]?.categories.find(
      (cat) => cat.name === category
    );

    if (!categoryConfig?.details) return null;

    return (
      <>
        {detailFields.map((item, detailIndex) => (
          <Box
            key={item.id}
            sx={[
              !!isCombined && {
                display: 'flex',
                gap: 1.5,
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
                width: '100%',
              },
            ]}
          >
            <DetailFields
              detailIndex={detailIndex}
              detailData={item as unknown as Record<string, unknown>}
              category={category}
              type={parsedTypeLocal}
              isDisabled={isDisabled}
            />
            {isEditable && detailFields.length > 1 && (
              <Button
                onClick={() => remove(detailIndex)}
                className="!h-auto !p-0"
              >
                <Icons.XinCircle className="text-navy-500 h-5 w-5 mt-14" />
              </Button>
            )}
          </Box>
        ))}
        {isEditable && (
          <Button
            type="button"
            onClick={() => append({})}
            variant="reserve-outlined"
            iconAfter={<Icons.PlusInCircle />}
            size="sm"
            className="mt-3"
          >
            and
          </Button>
        )}
      </>
    );
  };

  return (
    <form>
      {category === 'Other' ? (
        <OtherGoalForm isDisabled={isDisabled} />
      ) : (
        <>
          <Stack
            direction="row"
            sx={{ gap: 1.5, width: '100%', flexWrap: 'wrap' }}
          >
            <Select
              label="Goal type"
              value={{ label: type ?? '', value: type ?? '' }}
              options={[]}
              disabled
              sx={{ maxWidth: 150, mt: 1.5 }}
              data-testid="goal-type-select"
            />

            {schema[parsedType]?.funnelMetric && (
              <Controller
                name="funnelMetric"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Select
                    value={getSelectedOptionValue(
                      schema[parsedType].funnelMetric ?? [],
                      value
                    )}
                    onChange={(selectedOption) =>
                      onChange((selectedOption as SelectOption).value)
                    }
                    options={renderOptions(
                      schema[parsedType].funnelMetric ?? []
                    )}
                    disabled={isDisabled}
                    sx={{ maxWidth: 150, mt: 1.5 }}
                    label="Funnel Metric"
                    error={!!error}
                    errorMessage={error && 'This field is required'}
                    clearInputErr={() => clearErrors('funnelMetric')}
                    required={true}
                    data-testid="goal-funnel-metric-select"
                  />
                )}
              />
            )}

            {schema[parsedType]?.subType && (
              <Controller
                name="subType"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <Select
                    value={getSelectedOptionValue(
                      schema[parsedType].subType ?? [],
                      value
                    )}
                    onChange={(selectedOption) =>
                      handleSelectChange(
                        selectedOption as SelectOption,
                        parsedType,
                        schema,
                        onChange,
                        setValueType
                      )
                    }
                    options={renderOptions(schema[parsedType].subType ?? [])}
                    disabled={isDisabled}
                    sx={{ maxWidth: 150, mt: 1.5 }}
                    label="Sub Type"
                    error={!!error}
                    errorMessage={error && 'This field is required'}
                    clearInputErr={() => clearErrors('subType')}
                    required={true}
                  />
                )}
              />
            )}

            {category !== 'Combined' && renderDetailFields()}

            <Controller
              name="value"
              control={control}
              rules={{
                validate: (value) => validateValue(value, valueType),
              }}
              render={({ field, fieldState: { error } }) => (
                <Box>
                  <GoalValueInput
                    {...field}
                    placeholder="Define..."
                    disabled={isDisabled}
                    label={getValueLabel(valueType)}
                    error={!!error}
                    errorMessage={error?.message}
                    sx={{ mt: 1.5, maxWidth: 150 }}
                    withSuffix={!TYPES_WITHOUT_SUFFIXES.includes(type)}
                    ref={null}
                    required={true}
                    data-testid="goal-value-input"
                  />
                </Box>
              )}
            />
          </Stack>

          {category === 'Combined' && renderDetailFields()}

          <Stack direction="row" sx={{ mt: 2 }}>
            <Controller
              name="relativeToLastYear"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <RadioGroup
                  aria-label="Relative to last year"
                  required
                  row
                  size="small"
                  name={field.name}
                  value={(field.value as string | undefined) ?? ''}
                  onChange={(_event, value) => field.onChange(value)}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  disabled={isDisabled}
                  sx={{ mb: 1.5 }}
                >
                  {radioOptions.map((option) => (
                    <Radio
                      key={option.value}
                      value={option.value}
                      label={
                        <Box component="span" sx={{ color: option.labelColor }}>
                          {option.label}
                        </Box>
                      }
                      id={option.value}
                      data-testid={getTestId({
                        prefix: 'goal-relative-to-last-year',
                        label: option.value,
                        suffix: 'radio',
                      })}
                    />
                  ))}
                </RadioGroup>
              )}
            />
          </Stack>

          <Box sx={{ my: 2 }}>
            <Controller
              name="summary"
              control={control}
              rules={{ required: 'This field is required' }}
              disabled={isDisabled || generationLoading}
              render={({ field, fieldState: { error } }) => (
                <GeneratingTextArea
                  {...field}
                  label="AI Description"
                  rows={3}
                  loading={generationLoading}
                  supportiveText="The AI Description will be used on the Client's Goals page. It is editable."
                  error={!!error}
                  errorMessage={error?.message}
                  required
                  sx={{ maxWidth: 600 }}
                  data-testid="goal-summary-textarea"
                />
              )}
            />
            <Stack
              direction="row"
              sx={{ alignItems: 'center', gap: 1, mt: 1.5 }}
            >
              <Button
                variant="reserve-outlined"
                onClick={generateSummary}
                disabled={isDisabled || generationLoading}
                size="sm"
                iconBefore={<Icons.Ai />}
                type="button"
                data-testid="goal-generate-description-button"
              >
                Generate description
              </Button>
            </Stack>
          </Box>
        </>
      )}

      <Box sx={{ mt: 2.5 }}>
        <Controller
          name="note"
          control={control}
          render={({ field }) => (
            <TextArea
              {...field}
              label="Note"
              placeholder="Use this area to leave a note..."
              disabled={isDisabled}
              rows={3}
              sx={{ maxWidth: 600 }}
              data-testid="goal-note-textarea"
            />
          )}
        />
      </Box>
    </form>
  );
};

export default React.memo(GoalForm);
